import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Col, Input, message, Radio, Row, Select } from "antd";
import { DeleteFilled } from "@ant-design/icons";
import { INPUT_SIZE } from "../../../common/ThemeConstants";
import FormSelect from "../../../components/general/FormSelect";
import FormTextField from "../../../components/general/FormTextField";
import { postRequest } from "../../../services/mainApp.service";
import { clampQtyRateValue } from "../../../utils/commonfunction";
import BasicFormComponentInvTransaction from "../../../components/formComponent/BasicFormComponentInvTransaction";

const initialFormValues = {
  RecipeId: null,
  ProductDetailId: null,
  ProductCategoryId: null,
  SubRecipeCategoryId: null,
  SubRecipeItemId: null,
  RecipeDetails: [],
};

const CreateEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);
  const [tableLoading, setTableLoading] = useState(false);
  const isEditMode = !!id;
  const [formFields, setFormFields] = useState(initialFormValues);
  const [formLoading, setFormLoading] = useState(false);
  const [supportingTable, setSupportingTable] = useState({});
  const [recipeDetails, setRecipeDetails] = useState([]);
  const [selectedItemForAdd, setSelectedItemForAdd] = useState(null);
  const [addDetailQty, setAddDetailQty] = useState("");
  const [addDetailRateRU, setAddDetailRateRU] = useState("");
  const [addDetailRecipeUnitId, setAddDetailRecipeUnitId] = useState(null);
  const [addDetailOrderModeId, setAddDetailOrderModeId] = useState([]);
  const [recipeType, setRecipeType] = useState("Product"); // "Product" | "SubRecipe"
  const itemSelectRef = useRef(null);

  useEffect(() => {
    fetchInitialData();

    return () => {
      controller.abort();
    };
  }, [id]);

  useEffect(() => {
    const modes = supportingTable?.OrderModes || [];
    if (modes.length > 0 && addDetailOrderModeId.length === 0) {
      const allIds = modes.map((m) => m.OrderModeId ?? m.Id);
      setAddDetailOrderModeId(allIds);
    }
  }, [supportingTable?.OrderModes]);

  const fetchInitialData = async () => {
    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      RecipeId: isEditMode ? parseInt(id) : null,
      ProductDetailId: null,
      SubRecipeItemId: null,
      IsDetailData: isEditMode ? true : false,
      SearchText: "",
      PageNumber: 1,
      PageSize: 10,
      RecipeDetails: [],
    };

    try {
      const response = await postRequest("/Inv_Recipe", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching data"
        );
        return;
      }

      const dataSet = response.data;

      if (isEditMode) {
        const masterData = Array.isArray(dataSet?.Data)
          ? dataSet.Data[0]
          : dataSet?.Data;
        if (masterData) {
          const type = masterData.SubRecipeItemId ? "SubRecipe" : "Product";
          setRecipeType(type);
          setFormFields((prev) => ({
            ...prev,
            RecipeId: masterData.RecipeId,
            ProductDetailId: masterData.ProductDetailId,
            ProductCategoryId: masterData.ProductCategoryId,
            SubRecipeCategoryId: masterData.SubRecipeCategoryId ?? null,
            SubRecipeItemId: masterData.SubRecipeItemId ?? null,
          }));
          const detailsData = masterData?.RecipeDetails || dataSet?.RecipeDetails || [];
          const collapsedDetails = collapseDetailsByOrderModes(
            Array.isArray(detailsData) ? detailsData : []
          );
          setRecipeDetails(collapsedDetails);
        }
      }

      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable({
          Products:
            dataSet.AdditionalData?.Products ||
            dataSet.DataSet?.Table1 ||
            [],
          Items:
            dataSet.AdditionalData?.Items ||
            dataSet.DataSet?.Table2 ||
            [],
          ProductCategories:
            dataSet.AdditionalData?.ProductCategories ||
            dataSet.AdditionalData?.Categories ||
            dataSet.DataSet?.Table3 ||
            [],
          OrderModes:
            dataSet.AdditionalData?.OrderModes ||
            dataSet.DataSet?.Table4 ||
            [],
          RecipeUnits:
            dataSet.AdditionalData?.RecipeUnits ||
            dataSet.AdditionalData?.Units ||
            dataSet.DataSet?.Table5 ||
            [],
          InventoryCategories:
            dataSet.AdditionalData?.InventoryCategories ||
            dataSet.DataSet?.Table6 ||
            [],
          SubRecipes:
            dataSet.AdditionalData?.SubRecipes ||
            dataSet.DataSet?.Table7 ||
            [],
        });
      }
    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching recipe:", error);
        message.error("Failed to fetch recipe");
      }
    }
  };

  const handleFormChange = (data) => {

    setFormFields((prev) => {
      const updated = { ...prev, [data.name]: data.value };
      if (data.name === "SubRecipeCategoryId") {
        updated.SubRecipeItemId = null;
      }
      if (data.name === "ProductCategoryId") {
        const currentProduct = productList.find(
          (p) => (p.ProductDetailId ?? p.Id) === prev.ProductDetailId
        );
        const productInCategory =
          !data.value ||
          (currentProduct &&
            (currentProduct.ProductCategoryId ?? currentProduct.CategoryId) === data.value);
        if (!productInCategory) {
          updated.ProductDetailId = null;
        }
      }
      return updated;
    });
  };

  const handleRecipeTypeChange = (value) => {
    setRecipeType(value);
    if (value === "Product") {
      setFormFields((prev) => ({
        ...prev,
        SubRecipeCategoryId: null,
        SubRecipeItemId: null,
      }));
    } else {
      setFormFields((prev) => ({
        ...prev,
        ProductDetailId: null,
        ProductCategoryId: null,
      }));
    }
  };

  const handleProductChange = (data) => {
    const product = productList.find(
      (p) => (p.ProductDetailId ?? p.Id) === data.value
    );
    setFormFields((prev) => ({
      ...prev,
      ProductDetailId: data.value,
      ProductCategoryId: product
        ? product.ProductCategoryId ?? product.CategoryId ?? prev.ProductCategoryId
        : prev.ProductCategoryId,
    }));
  };

  const itemList = supportingTable?.Items || [];
  const productListRaw = supportingTable?.Products || [];
  const productList = useMemo(
    () =>
      productListRaw.map((p) => ({
        ...p,
        Id: p.ProductDetailId ?? p.Id,
        Name: p.ProductDetailName ?? p.Name ?? p.RecipeName ?? "",
      })),
    [productListRaw]
  );
  const productCategories = supportingTable?.ProductCategories || [];
  const inventoryCategories = supportingTable?.InventoryCategories || [];
  const subRecipesList = supportingTable?.SubRecipes || [];
  const filteredProductList = useMemo(() => {
    if (!formFields.ProductCategoryId) return productList;
    return productList.filter(
      (p) =>
        (p.ProductCategoryId ?? p.CategoryId) === formFields.ProductCategoryId
    );
  }, [productList, formFields.ProductCategoryId]);
  const orderModes = supportingTable?.OrderModes || [];
  const recipeUnits = supportingTable?.RecipeUnits || [];

  const collapseDetailsByOrderModes = (details) => {
    const map = new Map();
    details.forEach((d) => {
      const key = `${d.ItemId ?? d.ProductDetailId}_${d.QtyRU}_${d.RateRU}_${d.RecipeUnitId ?? ""}`;
      if (!map.has(key)) {
        map.set(key, { ...d, OrderModeIds: [] });
      }
      const entry = map.get(key);
      const modeId = d.OrderModeId ?? (d.OrderModeIds && d.OrderModeIds[0]);
      if (modeId != null && !entry.OrderModeIds.includes(modeId)) {
        entry.OrderModeIds.push(modeId);
      }
    });
    return Array.from(map.values()).map((d) => ({
      ...d,
      OrderModeIds: d.OrderModeIds?.length ? d.OrderModeIds : (d.OrderModeId != null ? [d.OrderModeId] : []),
    }));
  };

  const getItemId = (item) => item?.ItemId ?? item?.Id;
  const getItemName = (item) => item?.ItemName ?? item?.Name ?? "";
  const getItemCode = (item) => item?.ItemCode ?? item?.Code ?? "";
  const getBarcode = (item) => item?.Barcode ?? item?.BarCode ?? "";
  const getRecipeUnit = (item) => item?.RecipeUnit ?? item?.UnitName ?? "";
  const getRateRU = (item) => item?.RateRU ?? item?.LastPurchaseRateRU ?? item?.Rate ?? 0;


  const handleAddDetail = () => {
    const qty = clampQtyRateValue(addDetailQty);
    const rateRU = clampQtyRateValue(addDetailRateRU);
    const amount = qty * rateRU;

    if (selectedItemForAdd) {
      const itemId = getItemId(selectedItemForAdd);
      const orderModeIds = Array.isArray(addDetailOrderModeId) && addDetailOrderModeId.length > 0
        ? addDetailOrderModeId
        : [];

      if (orderModeIds.length === 0) {
        message.error("Please select at least one Order Mode");
        return;
      }

      const recipeUnit =
        recipeUnits.find((u) => (u.Id ?? u.RecipeUnitId) === addDetailRecipeUnitId)?.Name ??
        getRecipeUnit(selectedItemForAdd);

      const isDuplicate = recipeDetails.some(
        (d) => (d.ItemId ?? d.ProductDetailId) != null && (d.ItemId ?? d.ProductDetailId) === itemId
      );
      if (isDuplicate) {
        message.error("This item is already added to the table. Duplicate items are not allowed.");
        return;
      }

      if (qty <= 0) {
        message.error("Quantity must be greater than zero.");
        return;
      }

      const newDetail = {
        ItemId: itemId,
        ItemName: getItemName(selectedItemForAdd),
        RecipeUnitId: addDetailRecipeUnitId || null,
        RecipeUnit: recipeUnit,
        RecipeUnitId: selectedItemForAdd.RecipeUnitId || null,
        QtyRU: qty,
        RateRU: rateRU,
        Amount: amount,
        OrderModeIds: orderModeIds,
      };

      setRecipeDetails([newDetail, ...recipeDetails]);
      setSelectedItemForAdd(null);
      setAddDetailQty("");
      setAddDetailRateRU("");
      setAddDetailRecipeUnitId(null);
      const allModeIds = orderModes.map((m) => m.OrderModeId ?? m.Id);
      setAddDetailOrderModeId(allModeIds.length > 0 ? allModeIds : []);
      setTimeout(() => itemSelectRef.current?.focus(), 0);
    }
  };

  const handleDetailChange = (index, field, value) => {
    if (field === "QtyRU" || field === "RateRU") {
      value = clampQtyRateValue(value);
    }
    setRecipeDetails((prev) => {
      const updatedDetails = [...prev];
      const currentRecord = updatedDetails[index] || {};
      let newRecord = { ...currentRecord, [field]: value };
      if (field === "QtyRU" || field === "RateRU") {
        const qty = clampQtyRateValue(newRecord.QtyRU);
        const rate = clampQtyRateValue(newRecord.RateRU);
        newRecord = { ...newRecord, Amount: qty * rate };
      }
      if (field === "RecipeUnitId") {
        const unit = recipeUnits.find((u) => (u.Id ?? u.RecipeUnitId) === value);
        newRecord = { ...newRecord, RecipeUnit: unit?.Name ?? unit?.RecipeUnit ?? "" };
      }
      if (field === "OrderModeIds") {
        const newIds = Array.isArray(value) ? value : [];
        if (newIds.length === 0) {
          message.error("At least one Order Mode must be selected");
          return prev;
        }
        newRecord = { ...newRecord, OrderModeIds: newIds };
      }
      updatedDetails[index] = newRecord;
      return updatedDetails;
    });
  };

  const handleRemoveDetail = (index) => {
    setRecipeDetails(recipeDetails.filter((_, i) => i !== index));
  };

  const handleFormSave = async (e, operationId) => {
    e.preventDefault();

    if (recipeType === "Product") {
      if (!formFields.ProductDetailId) {
        message.error("Please select a product for this recipe");
        return;
      }
    } else {
      if (!formFields.SubRecipeItemId) {
        message.error("Please select a sub recipe for this recipe");
        return;
      }
    }

    if (recipeDetails.length === 0) {
      message.error("Please add at least one recipe detail");
      return;
    }

    const detailsWithoutOrderMode = recipeDetails.filter((d) => {
      const ids = d.OrderModeIds ?? (d.OrderModeId != null ? [d.OrderModeId] : []);
      return ids.length === 0;
    });
    if (detailsWithoutOrderMode.length > 0) {
      message.error("Each recipe detail must have at least one Order Mode selected");
      return;
    }

    setFormLoading(true);

    const payload = {
      OperationId: operationId,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      RecipeId: isEditMode ? parseInt(id) : null,
      ProductDetailId: recipeType === "Product" ? formFields.ProductDetailId : null,
      ProductCategoryId: recipeType === "Product" ? (formFields.ProductCategoryId || null) : null,
      SubRecipeItemId: recipeType === "SubRecipe" ? formFields.SubRecipeItemId : null,
      SubRecipeCategoryId: recipeType === "SubRecipe" ? (formFields.SubRecipeCategoryId || null) : null,
      IsDetailData: false,
      SearchText: "",
      PageNumber: 1,
      PageSize: 10,
      RecipeDetails: recipeDetails.flatMap((detail) => {
        const ids = detail.OrderModeIds ?? (detail.OrderModeId != null ? [detail.OrderModeId] : []);
        if (ids.length === 0) {
          return [{
            RecipeDetailId: detail.RecipeDetailId || null,
            ItemId: detail.ItemId,
            QtyRU: detail.QtyRU ?? 0,
            RecipeUnitId: detail.RecipeUnitId || null,
            RateRU: detail.RateRU ?? 0,
            OrderModeId: null,
          }];
        }
        return ids.map((modeId) => ({
          RecipeDetailId: detail.RecipeDetailId || null,
          ItemId: detail.ItemId,
          QtyRU: detail.QtyRU ?? 0,
          RecipeUnitId: detail.RecipeUnitId || null,
          RateRU: detail.RateRU ?? 0,
          OrderModeId: modeId,
        }));
      }),
    };

    try {
      const response = await postRequest("/Inv_Recipe", payload, controller);
      setFormLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage ||
          `Error ${isEditMode ? "updating" : "creating"} recipe`
        );
        return;
      }

      message.success(
        response.successMessage ||
        `Recipe ${isEditMode ? "updated" : "created"} successfully`
      );
      navigate("/recipe");
    } catch (error) {
      setFormLoading(false);
      if (error.message !== "canceled") {
        console.error("Error saving recipe:", error);
        message.error(
          error.response?.data?.Error_Message ||
          `Failed to ${isEditMode ? "update" : "create"} recipe`
        );
      }
    }
  };

  const detailColumns = useMemo(
    () => [
      { title: "Item", dataIndex: "ItemName", key: "ItemName" },
      { title: "Recipe Unit", dataIndex: "RecipeUnit", key: "RecipeUnit" },
      {
        title: "Qty",
        dataIndex: "QtyRU",
        key: "QtyRU",
        width: 100,
        render: (_, record, index) => (
          <Input
            type="number"
            value={record.QtyRU}
            onChange={(e) =>
              handleDetailChange(index, "QtyRU", parseFloat(e.target.value) || 0)
            }
            style={{ width: "100%" }}
            min={1}
            step={1}
          />
        ),
      },
      {
        title: "Rate",
        dataIndex: "RateRU",
        key: "RateRU"
      },
      {
        title: "Amount",
        dataIndex: "Amount",
        key: "Amount"
      },
      {
        title: "Order Mode",
        dataIndex: "OrderModeIds",
        key: "OrderModeIds",
        width: 350,
        render: (_, record, index) => (
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            placeholder="Order Mode"
            allowClear
            value={record.OrderModeIds ?? []}
            onChange={(val) => handleDetailChange(index, "OrderModeIds", val)}
            options={orderModes.map((m) => ({
              value: m.OrderModeId ?? m.Id,
              label: m.OrderMode ?? m.Name ?? "",
            }))}
          />
        ),
      },
      {
        title: "Action",
        key: "actions",
        width: 80,
        render: (_, record, index) => (
          <Button
            type="link"
            danger
            icon={<DeleteFilled />}
            onClick={() => handleRemoveDetail(index)}
          />
        ),
      },
    ],
    [orderModes, recipeUnits]
  );

  const labelStyle = { display: "block", marginBottom: 6, fontWeight: 500 };

  const productCategoryList = useMemo(
    () =>
      (productCategories || []).map((c) => ({
        ...c,
        Id: c.ProductCategoryId ?? c.Id,
        Name: c.CategoryName ?? c.Name ?? "",
      })),
    [productCategories]
  );

  const inventoryCategoryList = useMemo(
    () =>
      (inventoryCategories || []).map((c) => ({
        ...c,
        Id: c.Id ?? c.InventoryCategoryId,
        Name: c.Name ?? c.CategoryName ?? "",
      })),
    [inventoryCategories]
  );

  const filteredSubRecipesList = useMemo(() => {
    if (!formFields.SubRecipeCategoryId) return subRecipesList;
    const categoryId = formFields.SubRecipeCategoryId;
    const catNum = Number(categoryId);
    const filtered = subRecipesList.filter((r) => {
      const rCategoryId =
        r.InventoryCategoryId ?? r.CategoryId ?? r.ProductCategoryId;
      if (rCategoryId == null) return false;
      const rCatNum = Number(rCategoryId);
      return !Number.isNaN(catNum) && !Number.isNaN(rCatNum) && catNum === rCatNum;
    });
    return filtered.length > 0 ? filtered : subRecipesList;
  }, [subRecipesList, formFields.SubRecipeCategoryId]);

  const subRecipeListForSelect = useMemo(
    () =>
      filteredSubRecipesList.map((r) => ({
        ...r,
        Id: r.SubRecipeItemId ?? r.Id ?? r.RecipeId,
        Name: r.SubRecipeName ?? r.Name ?? r.RecipeName ?? "",
      })),
    [filteredSubRecipesList]
  );

  const selectedProduct = useMemo(
    () =>
      productList.find(
        (p) => (p.ProductDetailId ?? p.Id) === formFields.ProductDetailId
      ),
    [productList, formFields.ProductDetailId]
  );

  const salePrice = useMemo(
    () =>
      parseFloat(
        selectedProduct?.SalePrice ??
        selectedProduct?.Price ??
        selectedProduct?.SellingPrice ??
        0
      ) || 0,
    [selectedProduct]
  );

  const totalAmount = useMemo(
    () =>
      recipeDetails.reduce((sum, d) => {
        const amt =
          d.Amount != null
            ? parseFloat(d.Amount)
            : (parseFloat(d.QtyRU) || 0) * (parseFloat(d.RateRU) || 0);
        return sum + (Number.isNaN(amt) ? 0 : amt);
      }, 0),
    [recipeDetails]
  );

  const profit = salePrice - totalAmount;

  const formPanel = (
    <Fragment>

      {!isEditMode && (
        <Col xs={12} sm={6} md={4}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Recipe Type</label>
            <Radio.Group
              value={recipeType}
              onChange={(e) => handleRecipeTypeChange(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              disabled={isEditMode}
            >
              <Radio.Button value="Product">Product</Radio.Button>
              <Radio.Button value="SubRecipe">Sub Recipe</Radio.Button>
            </Radio.Group>
          </div>
        </Col>
      )}
      {recipeType === "Product" && (
        <>
          <Col xs={12} sm={6} md={4}>
            <FormSelect
              colSpan={24}
              listItem={productCategoryList}
              idName="Id"
              valueName="Name"
              size={INPUT_SIZE}
              name="ProductCategoryId"
              label="Category"
              value={formFields.ProductCategoryId}
              onChange={handleFormChange}
              allowClear={true}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <FormSelect
              colSpan={24}
              listItem={filteredProductList}
              idName="Id"
              valueName="Name"
              size={INPUT_SIZE}
              name="ProductDetailId"
              label="Product"
              value={formFields.ProductDetailId}
              onChange={handleProductChange}
              required={true}
              allowClear={!isEditMode}
            />
          </Col>
        </>
      )}
      {recipeType === "SubRecipe" && (
        <>
          <Col xs={12} sm={6} md={4}>
            <FormSelect
              colSpan={24}
              listItem={inventoryCategoryList}
              idName="Id"
              valueName="Name"
              size={INPUT_SIZE}
              name="SubRecipeCategoryId"
              label="Category"
              value={formFields.SubRecipeCategoryId}
              onChange={handleFormChange}
              allowClear={true}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <FormSelect
              colSpan={24}
              listItem={subRecipeListForSelect}
              idName="Id"
              valueName="Name"
              size={INPUT_SIZE}
              name="SubRecipeItemId"
              label="Sub Recipe"
              value={formFields.SubRecipeItemId}
              onChange={handleFormChange}
              required={true}
              allowClear={!isEditMode}
            />
          </Col>
        </>
      )}
      <Col xs={12} sm={6} md={4}>
        <FormTextField
          colSpan={24}
          label="Total Items"
          name="TotalItems"
          size={INPUT_SIZE}
          value={recipeDetails.length}
          readOnly={true}
          type="text"
        />
      </Col>
      {recipeType === "Product" && (
        <Col xs={12} sm={6} md={4}>
          <FormTextField
            colSpan={24}
            label="Sale Price"
            name="SalePrice"
            size={INPUT_SIZE}
            value={salePrice.toFixed(2)}
            readOnly={true}
            type="text"
          />
        </Col>
      )}
      <Col xs={12} sm={6} md={4}>
        <FormTextField
          colSpan={24}
          label="Total Amount"
          name="TotalAmount"
          size={INPUT_SIZE}
          value={totalAmount.toFixed(2)}
          readOnly={true}
          type="text"
        />
      </Col>
      {recipeType === "Product" && (
        <Col xs={12} sm={6} md={4}>
          <FormTextField
            colSpan={24}
            label="Profit"
            name="Profit"
            size={INPUT_SIZE}
            value={profit.toFixed(2)}
            readOnly={true}
            type="text"
          />
        </Col>
      )}
    </Fragment>
  );

  const addDetailEntryPanel = (
    <div
      style={{
        width: "100%",
        padding: "16px",
        background: "#fafafa",
        borderRadius: 8,
        border: "1px solid #f0f0f0",
        marginBottom: 16,
      }}
    >
      <div
        style={{
          marginBottom: 12,
          fontSize: 14,
          fontWeight: 600,
          color: "#262626",
        }}
      >
        Add Item Detail
      </div>
      <Row gutter={[16, 12]} align="bottom">
        <Col xs={12} sm={8} md={4} lg={2}>
          <label style={labelStyle}>Bar code</label>
          <Select
            style={{ width: "100%" }}
            placeholder="Bar code"
            showSearch
            allowClear
            size="middle"
            value={selectedItemForAdd ? getBarcode(selectedItemForAdd) : undefined}
            onChange={(value) => {
              const item = itemList.find((i) => getBarcode(i) === value);
              setSelectedItemForAdd(item || null);
              if (item) {
                const rate = getRateRU(item);
                setAddDetailRateRU(rate > 0 ? rate.toString() : "");
              } else {
                setAddDetailRateRU("");
              }
            }}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={[
              ...itemList
                .filter((i) => getBarcode(i))
                .map((item) => ({
                  value: getBarcode(item),
                  label: getBarcode(item),
                })),
            ]}
          />
        </Col>
        <Col xs={12} sm={8} md={4} lg={2}>
          <label style={labelStyle}>Item code</label>
          <Select
            style={{ width: "100%" }}
            placeholder="Item code"
            showSearch
            allowClear
            size="middle"
            value={selectedItemForAdd ? getItemCode(selectedItemForAdd) : undefined}
            onChange={(value) => {
              const item = itemList.find((i) => getItemCode(i) === value);
              setSelectedItemForAdd(item || null);
              if (item) {
                const rate = getRateRU(item);
                setAddDetailRateRU(rate > 0 ? rate.toString() : "");
              } else {
                setAddDetailRateRU("");
              }
            }}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={[
              ...itemList
                .filter((i) => getItemCode(i))
                .map((item) => ({
                  value: getItemCode(item),
                  label: getItemCode(item),
                })),
            ]}
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={5}>
          <label style={labelStyle}>Item name</label>
          <Select
            ref={itemSelectRef}
            style={{ width: "100%" }}
            placeholder="Search by item name"
            showSearch
            allowClear
            size="middle"
            value={selectedItemForAdd ? getItemId(selectedItemForAdd) : undefined}
            onChange={(value) => {
              const item = itemList.find((i) => getItemId(i) === value);
              setSelectedItemForAdd(item || null);
              if (item) {
                const rate = getRateRU(item);
                setAddDetailRateRU(rate > 0 ? rate.toString() : "");
              } else {
                setAddDetailRateRU("");
              }
            }}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={[
              ...itemList.map((item) => ({
                value: getItemId(item),
                label: getItemName(item),
              })),
            ]}
          />
        </Col>
        <Col xs={12} sm={8} md={4} lg={3}>
          <label style={labelStyle}>Rate</label>
          <Input
            type="number"
            placeholder="0"
            size="middle"
            value={addDetailRateRU}
            onChange={(e) => setAddDetailRateRU(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddDetail();
            }}
            min={0}
            step={0.01}
            readOnly={true}
          />
        </Col>
        <Col xs={12} sm={8} md={4} lg={3}>
          <label style={labelStyle}>Quantity</label>
          <Input
            type="number"
            placeholder="0"
            size="middle"
            value={addDetailQty}
            onChange={(e) => setAddDetailQty(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddDetail();
            }}
            min={0}
            step={0.01}
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={5}>
          <label style={labelStyle}>Order Mode</label>
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            placeholder="Order Mode"
            allowClear
            size="middle"
            value={addDetailOrderModeId}
            onChange={setAddDetailOrderModeId}
            options={orderModes.map((m) => ({
              value: m.OrderModeId ?? m.Id,
              label: m.OrderMode ?? m.Name ?? "",
            }))}
          />
        </Col>
        <Col xs={12} sm={8} md={4} lg={3}>
          <Button
            type="primary"
            onClick={handleAddDetail}
            disabled={!selectedItemForAdd || !addDetailQty}
            style={{ marginTop: 8 }}
          >
            Add Item
          </Button>
        </Col>
      </Row>
    </div>
  );

  return (
    <BasicFormComponentInvTransaction
      pageMode="transaction"
      isEditMode={isEditMode}
      formTitle={isEditMode ? "Edit Recipe" : "Create Recipe"}
      crudTitle="Recipe"
      baseRoute="/recipe"
      formPanel={formPanel}
      transactionDetailsColumns={detailColumns}
      transactionDetailsRows={recipeDetails}
      onAddDetail={handleAddDetail}
      onFormSave={handleFormSave}
      showSubmit={false}
      formLoading={formLoading}
      onCancelForm={() => navigate("/recipe")}
      addDetailEntryPanel={addDetailEntryPanel}
      showSave={true}
      showAddDetailButton={false}
    />
  );
};

export default CreateEdit;
